import {
	EuiPage,
	EuiPageBody,
	EuiPageHeader,
	EuiPageSection,
	EuiPanel,
	EuiSpacer,
	EuiTitle,
} from '@equipmentshare/ds2'

import { FilterConfig, UseFetchFilterOptionsQuery } from '@equipmentshare/dcf-search-filter-experience'
import SearchFilterGrid from 'components/SearchFilterGrid'
import { ReactElement, useEffect, useRef } from 'react'
import { useGridApiRef } from '@mui/x-data-grid-premium'
import columns from './columns'
import useTransferOrdersDataQuery, { TransferOrderRow, TransferOrdersSearchRequest } from './hooks/useTransferOrdersDataQuery'
import useInventoryLocationsSearch from 'hooks/inventory-location-search'
import { APP_HEADER_HEIGHT } from '../helpers/utils'

const filters: FilterConfig<TransferOrdersSearchRequest>[] = [
	{
		field: 'statuses',
		label: 'Status',
		type: 'list',
		showByDefault: true,
	},
	{
		field: 'toInventoryIds',
		label: 'To Store',
		type: 'list',
		showByDefault: true,
	},
	{
		field: 'orderTypes',
		label: 'Order Type',
		type: 'list',
		showByDefault: false,
	},
	{
		field: 'isClosed',
		label: 'Closed',
		type: 'list',
		showByDefault: false,
	},
	{
		field: 'fromInventoryIds',
		label: 'From Store',
		type: 'list',
		showByDefault: false,
	},
	{
		field: 'fulfillmentOrderNumbers',
		label: 'Fulfillment Order #',
		type: 'list',
		showByDefault: true,
		displayOptionNarrowingInput: true,
		optionFilterMode: 'server',
	},
]

const STATUS_OPTIONS = [
	{ id: 'PENDING', label: 'Pending', count: 0 },
	{ id: 'APPROVED', label: 'Approved', count: 0 },
	{ id: 'REJECTED', label: 'Rejected', count: 0 },
	{ id: 'IN_TRANSIT', label: 'In Transit', count: 0 },
	{ id: 'COMPLETED', label: 'Completed', count: 0 },
	{ id: 'CANCELLED', label: 'Cancelled', count: 0 },
]

const ORDER_TYPE_OPTIONS = [
	{ id: 'MANUAL', label: 'Manual', count: 0 },
	{ id: 'SYSTEM', label: 'System', count: 0 },
]

const IS_CLOSED_OPTIONS = [
	{ id: 'true', label: 'Closed', count: 0 },
	{ id: 'false', label: 'Open', count: 0 },
]

export const useFetchFilterOptionsQuery: UseFetchFilterOptionsQuery<TransferOrdersSearchRequest> = ( countBy, { enabled }, apiFilterParams, filterQuery ) => {
	const isStoreFilter = countBy === 'fromInventoryIds' || countBy === 'toInventoryIds'
	// Fetch every page of inventory locations (getAll loops on hasNextPage and accumulates),
	// matching the inventory/requests "Stores" filter. A single first:1000 page is sorted by
	// id and truncates newer stores, so they would otherwise never appear as filter options.
	// Only fetch for the store filters, and only once the dropdown is engaged.
	const { inventoryLocations, loading: storesLoading } = useInventoryLocationsSearch( '', 'transfer-orders-filter-options', 'no-cache', undefined, undefined, true, !isStoreFilter || !enabled )

	if ( countBy === 'statuses' ) {
		return { isFetching: false, data: { filterOptions: STATUS_OPTIONS } }
	}
	if ( countBy === 'orderTypes' ) {
		return { isFetching: false, data: { filterOptions: ORDER_TYPE_OPTIONS } }
	}
	if ( countBy === 'isClosed' ) {
		return { isFetching: false, data: { filterOptions: IS_CLOSED_OPTIONS } }
	}
	if ( isStoreFilter ) {
		const filterOptions = inventoryLocations.map( ( location ) => ( {
			id: location.id,
			label: location.name,
			count: 0,
		} ) )
		return { isFetching: storesLoading, data: { filterOptions } }
	}
	if ( countBy === 'fulfillmentOrderNumbers' ) {
		const currentRaw = apiFilterParams.fulfillmentOrderNumbers
		const current = Array.isArray( currentRaw ) ? currentRaw : currentRaw ? [ currentRaw ] : []
		const query = filterQuery?.trim() ?? ''
		// While the user is narrowing, offer the number they typed plus any active
		// selection that actually matches it. Listing every active selection
		// regardless of the query made a second lookup look like it had not taken.
		const options = query
			? Array.from( new Set( [ query, ...current.filter( ( fo ) => fo.includes( query ) ) ] ) )
			: current
		return { isFetching: false, data: { filterOptions: options.map( ( fo ) => ( { id: fo, label: fo, count: 0 } ) ) } }
	}
	return { isFetching: false, data: { filterOptions: [] } }
}

/** Query-string key the grid stores its page number under. */
const PAGE_PARAM = 'page'

/**
 * How SearchFilterExperience announces a URL write. It syncs with
 * `history.replaceState`, which no router hook observes, so `useSearchParams` never moves.
 */
const URL_CHANGE_EVENT = 'urlsearchparamschange'

/** Everything except the page number identifies the result set. */
function identifyingKey( search: string ): string {
	const identifying = new URLSearchParams( search )
	identifying.delete( PAGE_PARAM )
	identifying.sort()
	return identifying.toString()
}

export default function TransferOrders(): ReactElement {
	const dataGridApiRef = useGridApiRef()
	const previousFilterKey = useRef( identifyingKey( window.location.search ) )

	// Rewind to the first page whenever the result set changes. The search box writes
	// through `set`, which keeps `page`, while useTransferOrdersDataQuery drops its
	// cursor map — so searching from page 3 came back empty until a reload.
	useEffect( () => {
		const handleUrlChange = ( event: Event ) => {
			const detail = ( event as CustomEvent<{ search?: string }> ).detail
			const filterKey = identifyingKey( detail?.search ?? window.location.search )
			if ( previousFilterKey.current === filterKey ) {
				return
			}
			previousFilterKey.current = filterKey
			dataGridApiRef.current?.setPage( 0 )
		}

		window.addEventListener( URL_CHANGE_EVENT, handleUrlChange )
		return () => window.removeEventListener( URL_CHANGE_EVENT, handleUrlChange )
	}, [ dataGridApiRef ] )

	return (
		<EuiPage paddingSize="l" style={{ height: `calc(100vh - ${APP_HEADER_HEIGHT}px)`, overflow: 'hidden', boxSizing: 'border-box' }}>
			<EuiPageBody style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
				<EuiPageHeader
					pageTitle={<EuiTitle size="m"><span>Transfer Orders</span></EuiTitle>}
					paddingSize="none"
				/>
				<EuiSpacer />
				<EuiPageSection
					paddingSize="none"
					style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
					contentProps={{ style: { height: '100%' } }}
				>
					<EuiPanel paddingSize="none" hasShadow={false} style={{ height: '100%', overflow: 'auto' }}>
						<SearchFilterGrid<TransferOrderRow, TransferOrdersSearchRequest>
							dataGridName="transfer-orders-table"
							dataGridApiRef={dataGridApiRef}
							filters={filters}
							filterStateMode="url"
							columns={columns}
							pageSizeOptions={[ 25, 50, 100 ]}
							useFetchQuery={useTransferOrdersDataQuery}
							useFetchFilterOptionsQuery={useFetchFilterOptionsQuery}
							searchPlaceholder="Search transfer orders..."
							getRowId={( row ) => row.id}
						/>
					</EuiPanel>
				</EuiPageSection>
			</EuiPageBody>
		</EuiPage>
	)
}
